import type { QueryClient } from '@tanstack/react-query';
import type { Board, Comment, IssueCard, ServerEvent } from '@tasks/contracts';

import { queryKeys } from '../api/queryKeys';
import type { Page } from '../lib/types';

/**
 * Точечное применение WS-событий к кэшу React Query (docs/05 §11).
 * Полный refetch делается только при reconnect — здесь только патчи,
 * чтобы карточки не «прыгали».
 */

type BoardUpdater = (board: Board) => Board;

function mapAllBoards(queryClient: QueryClient, projectKey: string, updater: BoardUpdater): void {
  queryClient.setQueriesData<Board>({ queryKey: queryKeys.board.root(projectKey) }, (previous) =>
    previous ? updater(previous) : previous,
  );
}

function mapCard(board: Board, issueId: string, updater: (card: IssueCard) => IssueCard): Board {
  let changed = false;
  const columns = board.columns.map((column) => ({
    ...column,
    lanes: column.lanes.map((lane) => ({
      ...lane,
      issues: lane.issues.map((card) => {
        if (card.id !== issueId) {
          return card;
        }
        changed = true;
        return updater(card);
      }),
    })),
  }));
  return changed ? { ...board, columns } : board;
}

function findCard(board: Board, issueId: string): IssueCard | null {
  for (const column of board.columns) {
    for (const lane of column.lanes) {
      for (const card of lane.issues) {
        if (card.id === issueId) {
          return card;
        }
      }
    }
  }
  return null;
}

function removeCard(board: Board, issueId: string): Board {
  if (!findCard(board, issueId)) {
    return board;
  }
  const columns = board.columns.map((column) => {
    let removedFromColumn = 0;
    const lanes = column.lanes.map((lane) => {
      const issues = lane.issues.filter((card) => card.id !== issueId);
      removedFromColumn += lane.issues.length - issues.length;
      return { ...lane, issues, count: Math.max(0, lane.count - (lane.issues.length - issues.length)) };
    });
    return { ...column, lanes, issueCount: Math.max(0, column.issueCount - removedFromColumn) };
  });
  return { ...board, columns, total: Math.max(0, board.total - 1) };
}

function insertCard(board: Board, card: IssueCard): Board {
  const columnIndex = board.columns.findIndex((column) => column.status.id === card.statusId);
  if (columnIndex < 0) {
    return board;
  }
  const columns = board.columns.map((column, index) => {
    if (index !== columnIndex || column.lanes.length === 0) {
      return column;
    }
    const [firstLane, ...rest] = column.lanes;
    if (!firstLane) {
      return column;
    }
    return {
      ...column,
      issueCount: column.issueCount + 1,
      lanes: [{ ...firstLane, issues: [card, ...firstLane.issues], count: firstLane.count + 1 }, ...rest],
    };
  });
  return { ...board, columns, total: board.total + 1 };
}

function moveCard(
  queryClient: QueryClient,
  projectKey: string,
  payload: { issueId: string; statusId: string; sprintId: string | null; rank: string },
): void {
  mapAllBoards(queryClient, projectKey, (board) => {
    const card = findCard(board, payload.issueId);
    if (!card) {
      return board;
    }
    const stripped = removeCard(board, payload.issueId);
    const next: IssueCard = {
      ...card,
      statusId: payload.statusId,
      sprintId: payload.sprintId,
      rank: payload.rank,
    };
    return insertCard(stripped, next);
  });
}

function patchCardFields(queryClient: QueryClient, projectKey: string, issueId: string, patch: Partial<IssueCard>): void {
  mapAllBoards(queryClient, projectKey, (board) => mapCard(board, issueId, (card) => ({ ...card, ...patch })));
}

/** Из `patch` берём только поля, которые есть в компактной карточке. */
function toCardPatch(patch: Record<string, unknown>): Partial<IssueCard> {
  const result: Partial<IssueCard> = {};
  const summary = patch['summary'];
  const priority = patch['priority'];
  const statusId = patch['statusId'];
  const storyPoints = patch['storyPoints'];
  const dueDate = patch['dueDate'];
  const labels = patch['labels'];
  const updatedAt = patch['updatedAt'];
  if (typeof summary === 'string') {
    result.summary = summary;
  }
  if (typeof priority === 'string') {
    result.priority = priority as IssueCard['priority'];
  }
  if (typeof statusId === 'string') {
    result.statusId = statusId;
  }
  if (typeof storyPoints === 'number' || storyPoints === null) {
    result.storyPoints = storyPoints;
  }
  if (typeof dueDate === 'string' || dueDate === null) {
    result.dueDate = dueDate;
  }
  if (Array.isArray(labels)) {
    result.labels = labels.filter((label): label is string => typeof label === 'string');
  }
  if (typeof updatedAt === 'string') {
    result.updatedAt = updatedAt;
  }
  return result;
}

type CommentPage = Page<Comment>;

function updateComments(
  queryClient: QueryClient,
  issueKey: string,
  updater: (previous: CommentPage) => CommentPage,
): void {
  queryClient.setQueryData<CommentPage>(queryKeys.issues.comments(issueKey), (previous) =>
    previous ? updater(previous) : previous,
  );
}

/** `issueId` → `key`: комментарии и вложения адресуются ключом задачи. */
function resolveIssueKey(queryClient: QueryClient, projectKey: string, issueId: string): string {
  const details = queryClient.getQueriesData<{ id: string; key: string }>({ queryKey: queryKeys.issues.all });
  for (const [, data] of details) {
    if (data && data.id === issueId) {
      return data.key;
    }
  }
  const boards = queryClient.getQueriesData<Board>({ queryKey: queryKeys.board.root(projectKey) });
  for (const [, board] of boards) {
    if (!board) {
      continue;
    }
    const card = findCard(board, issueId);
    if (card) {
      return card.key;
    }
  }
  return issueId;
}

/** Единая точка применения события. Возвращает `true`, если кэш изменён. */
export function applyServerEvent(queryClient: QueryClient, projectKey: string, event: ServerEvent): boolean {
  switch (event.type) {
    case 'issue.created':
      mapAllBoards(queryClient, projectKey, (board) => insertCard(board, event.payload.issue));
      queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(projectKey) });
      return true;
    case 'issue.updated': {
      const cardPatch = toCardPatch(event.payload.patch);
      if (Object.keys(cardPatch).length > 0) {
        patchCardFields(queryClient, projectKey, event.payload.issueId, cardPatch);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.detail(event.payload.key) });
      return true;
    }
    case 'issue.deleted':
      mapAllBoards(queryClient, projectKey, (board) => removeCard(board, event.payload.issueId));
      queryClient.removeQueries({ queryKey: queryKeys.issues.detail(event.payload.key) });
      return true;
    case 'issue.moved':
      moveCard(queryClient, projectKey, event.payload);
      return true;
    case 'comment.created': {
      const issueKey = resolveIssueKey(queryClient, projectKey, event.payload.issueId);
      const comment = event.payload.comment;
      updateComments(queryClient, issueKey, (previous) =>
        previous.items.some((item) => item.id === comment.id)
          ? previous
          : { ...previous, items: [...previous.items, comment], total: previous.total + 1 },
      );
      return true;
    }
    case 'comment.updated': {
      const issueKey = resolveIssueKey(queryClient, projectKey, event.payload.issueId);
      const comment = event.payload.comment;
      updateComments(queryClient, issueKey, (previous) => ({
        ...previous,
        items: previous.items.map((item) => (item.id === comment.id ? comment : item)),
      }));
      return true;
    }
    case 'comment.deleted': {
      const issueKey = resolveIssueKey(queryClient, projectKey, event.payload.issueId);
      const commentId = event.payload.commentId;
      updateComments(queryClient, issueKey, (previous) => ({
        ...previous,
        items: previous.items.filter((item) => item.id !== commentId),
        total: Math.max(0, previous.total - 1),
      }));
      return true;
    }
    case 'attachment.added':
    case 'attachment.deleted': {
      const issueKey = resolveIssueKey(queryClient, projectKey, event.payload.issueId);
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.attachments(issueKey) });
      return true;
    }
    case 'sprint.updated':
      queryClient.invalidateQueries({ queryKey: ['projects', projectKey, 'sprints'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(projectKey) });
      return true;
    case 'workflow.updated':
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.workflow(projectKey) });
      queryClient.invalidateQueries({ queryKey: queryKeys.board.root(projectKey) });
      return true;
    case 'presence.updated':
      return false;
    case 'notification.created':
      // Персональная доставка: обновляем счётчик и список уведомлений.
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      return true;
    case 'pong':
      return false;
    default:
      return false;
  }
}

/** Presence-события обрабатываются отдельно: они не про кэш задач. */
export function isPresenceEvent(event: ServerEvent): event is Extract<ServerEvent, { type: 'presence.updated' }> {
  return event.type === 'presence.updated';
}
